import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { Collection, StaffDataContext, StaffDataContextValue } from './StaffDataContext';

interface Props {
  children: React.ReactNode;
  // Collections whose cache should be invalidated when the user clicks Retry.
  // Without this, a rejected entry stays rejected forever and the next render
  // re-throws the same cached error.
  invalidateOnRetry?: Collection | Collection[];
  fallback?: (error: unknown, retry: () => void) => React.ReactNode;
}

interface State {
  error: unknown;
}

export class DataErrorBoundary extends React.Component<Props, State> {
  static contextType = StaffDataContext;
  declare context: StaffDataContextValue | null;

  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown) {
    console.error('DataErrorBoundary caught:', error);
  }

  retry = () => {
    const ctx = this.context;
    const targets = this.props.invalidateOnRetry;
    if (ctx && targets) {
      const list = Array.isArray(targets) ? targets : [targets];
      for (const collection of list) ctx.invalidate(collection);
    }
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.retry);
      return (
        <div className="bg-white border border-red-100 rounded-2xl p-6 my-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-slate-800 text-sm">
              {normalizeErrorMessage(this.state.error, 'Something went wrong loading this view.')}
            </p>
            <button
              onClick={this.retry}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-teal-700 hover:text-teal-900"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
